<template>
  <div class="market-page">
    <div class="market-bg"></div>

    <header class="market-header">
      <div>
        <p class="eyebrow">ROBOT MARKET</p>
        <h1>机器人市场</h1>
        <p>按状态、类型和服务区域查看已注册机器人；管理员可对已通过机器人执行下架。</p>
      </div>
      <div class="header-actions">
        <button class="ghost-btn" @click="$router.push({ name: 'adminLogin' })">返回控制台入口</button>
      </div>
    </header>

    <section class="toolbar glass-card">
      <div class="status-tabs" role="tablist" aria-label="机器人状态筛选">
        <button
          v-for="item in statusOptions"
          :key="item.value"
          type="button"
          :class="{ active: status === item.value }"
          @click="changeStatus(item.value)"
        >
          {{ item.label }}
        </button>
      </div>

      <el-select v-model="category" clearable placeholder="按机器人类型筛选" @change="fetchRobots">
        <el-option
          v-for="item in robotTypes"
          :key="item.value"
          :label="item.label"
          :value="item.value"
        ></el-option>
      </el-select>

      <el-select v-model="serviceRegion" clearable filterable placeholder="按服务区域筛选" @change="fetchRobots">
        <el-option
          v-for="item in regionOptions"
          :key="item"
          :label="item"
          :value="item"
        ></el-option>
      </el-select>
    </section>

    <section v-loading="loading" class="robot-grid">
      <article v-for="robot in robots" :key="robot.id" class="robot-card glass-card">
        <div class="cover">
          <img v-if="robot.coverImageUrl" :src="robot.coverImageUrl" :alt="robot.name" />
          <span v-else>ROBOT</span>
        </div>
        <div class="robot-body">
          <div class="card-top">
            <span class="type-pill">{{ robotTypeLabel(robot.category) }}</span>
            <span class="status-pill">{{ robotStatusLabel(robot.registerStatus) }}</span>
          </div>
          <h2>{{ robot.name || '未命名机器人' }}</h2>
          <p class="desc">{{ robot.capabilityDesc || '暂无能力描述' }}</p>
          <div class="meta-list">
            <span>联系电话：{{ robot.contactPhone || '未填写' }}</span>
            <span>地域限制：{{ robot.serviceRegionLimit || '不限制' }}</span>
            <span>评分：{{ robot.score || 100 }}</span>
            <span>余额：{{ robot.money || 0 }}</span>
          </div>
          <div class="card-actions" v-if="status === 'approved'">
            <button class="mini-btn danger" @click="takeDown(robot)">下架机器人</button>
          </div>
        </div>
      </article>

      <el-empty v-if="!loading && robots.length === 0" description="暂无机器人"></el-empty>
    </section>
  </div>
</template>

<script>
import { getAdminRobotMarket, takeDownRobot } from '../api/market'
import { getAdminToken } from '../utils/auth'

export default {
  name: 'RobotMarket',
  data() {
    return {
      loading: false,
      robots: [],
      status: 'approved',
      category: '',
      serviceRegion: '',
      robotTypes: [
        { value: 'CLEANING', label: '清洁机器人' },
        { value: 'DELIVERY', label: '配送机器人' },
        { value: 'SHUTTLE', label: '接驳机器人' },
        { value: 'INSPECTION', label: '巡检机器人' },
        { value: 'GO2', label: '机器狗 Go2' }
      ],
      statusOptions: [
        { value: 'reviewing', label: '审核中' },
        { value: 'approved', label: '已通过' },
        { value: 'rejected', label: '未通过' }
      ]
    }
  },
  computed: {
    regionOptions() {
      return [...new Set(this.robots
        .map(item => (item.serviceRegionLimit || '').trim())
        .filter(Boolean))]
        .sort((left, right) => left.localeCompare(right, 'zh-CN'))
    }
  },
  created() {
    if (!getAdminToken()) {
      this.$message.warning('机器人市场仅管理员可见')
      this.$router.replace({ name: 'home' })
      return
    }
    this.fetchRobots()
  },
  methods: {
    changeStatus(value) {
      if (this.status === value) return
      this.status = value
      this.fetchRobots()
    },
    async fetchRobots() {
      this.loading = true
      try {
        const response = await getAdminRobotMarket({
          status: this.status,
          category: this.category || undefined,
          serviceRegion: this.serviceRegion || undefined
        })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '机器人市场加载失败')
          return
        }
        this.robots = response.data.data || []
      } catch (error) {
        this.$message.error('机器人市场请求失败，请检查后端服务')
      } finally {
        this.loading = false
      }
    },
    async takeDown(robot) {
      await this.$confirm('下架后该机器人会从已通过列表移除，确定继续吗？', '下架机器人', {
        confirmButtonText: '确定下架',
        cancelButtonText: '取消',
        type: 'warning'
      })
      await this.runAction(() => takeDownRobot(robot.id), '机器人已下架')
    },
    async runAction(request, successText) {
      try {
        const response = await request()
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '操作失败')
          return
        }
        this.$message.success(successText)
        this.fetchRobots()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('操作失败，请稍后重试')
        }
      }
    },
    robotTypeLabel(value) {
      const matched = this.robotTypes.find(item => item.value === value)
      return matched ? matched.label : (value || '未分类')
    },
    robotStatusLabel(value) {
      if (value === 'APPROVED') return '已通过'
      if (value === 'REJECTED') return '未通过'
      return '审核中'
    }
  }
}
</script>

<style scoped>
.market-page {
  position: relative;
  min-height: 100vh;
  padding: 34px;
  overflow: hidden;
  background:
    radial-gradient(circle at 14% 10%, rgba(142, 215, 255, 0.2), transparent 26%),
    radial-gradient(circle at 86% 16%, rgba(255, 184, 92, 0.16), transparent 25%),
    linear-gradient(135deg, #06111e 0%, #0a1524 48%, #050b15 100%);
  color: #fff6ea;
}

.market-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.045) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px);
  background-size: 48px 48px;
  opacity: 0.45;
}

.market-header,
.toolbar,
.robot-grid {
  position: relative;
  z-index: 1;
}

.market-header {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: flex-end;
  margin-bottom: 24px;
}

.eyebrow {
  margin: 0 0 10px;
  color: rgba(255, 205, 145, 0.86);
  letter-spacing: 0.35em;
  font-size: 12px;
}

.market-header h1 {
  margin: 0;
  font-size: clamp(36px, 5vw, 68px);
  font-family: ui-serif, serif;
}

.market-header p {
  margin: 12px 0 0;
  color: rgba(255, 246, 234, 0.7);
}

.header-actions {
  display: flex;
  gap: 12px;
}

.ghost-btn,
.primary-btn,
.mini-btn {
  border: none;
  border-radius: 999px;
  cursor: pointer;
}

.ghost-btn {
  padding: 11px 16px;
  color: #fff6ea;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.05);
}

.primary-btn {
  padding: 11px 18px;
  color: #06111e;
  font-weight: 700;
  background: linear-gradient(135deg, #ffd08a, #8ed7ff);
}

.glass-card {
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(8, 16, 28, 0.72);
  backdrop-filter: blur(18px);
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.22);
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 22px;
  padding: 16px 18px;
  border-radius: 24px;
}

.toolbar :deep(.el-select) {
  width: min(220px, 100%);
}

.toolbar :deep(.el-input__wrapper) {
  background: rgba(3, 11, 21, 0.75);
  box-shadow: 0 0 0 1px rgba(142, 215, 255, 0.2) inset;
}

.toolbar :deep(.el-input__inner) {
  color: #eef7ff;
}

.status-tabs {
  flex: 1;
  display: flex;
  gap: 8px;
  padding: 6px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.05);
}

.status-tabs button {
  height: 38px;
  min-width: 96px;
  padding: 0 16px;
  border: 0;
  border-radius: 14px;
  background: transparent;
  color: rgba(255, 246, 234, 0.72);
  cursor: pointer;
  font-weight: 700;
}

.status-tabs button.active {
  color: #06111e;
  background: linear-gradient(135deg, #ffd08a, #8ed7ff);
}

.robot-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 18px;
  min-height: 260px;
}

.robot-card {
  display: grid;
  grid-template-columns: 132px 1fr;
  gap: 18px;
  padding: 18px;
  border-radius: 26px;
}

.cover {
  height: 132px;
  border-radius: 22px;
  overflow: hidden;
  display: grid;
  place-items: center;
  color: rgba(255, 246, 234, 0.6);
  background: rgba(255, 255, 255, 0.06);
}

.cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.card-top {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.type-pill,
.status-pill {
  padding: 8px 10px;
  border-radius: 999px;
  font-size: 12px;
}

.type-pill {
  color: #06111e;
  background: linear-gradient(135deg, #ffd08a, #8ed7ff);
}

.status-pill {
  color: #dff3ff;
  background: rgba(142, 215, 255, 0.14);
}

.robot-card h2 {
  margin: 14px 0 8px;
  font-size: 22px;
}

.desc {
  min-height: 48px;
  color: rgba(255, 246, 234, 0.68);
  line-height: 1.7;
}

.meta-list {
  display: grid;
  gap: 6px;
  margin-top: 12px;
  color: rgba(223, 243, 255, 0.76);
  font-size: 13px;
}

.card-actions {
  display: flex;
  gap: 10px;
  margin-top: 16px;
}

.mini-btn {
  padding: 9px 13px;
  color: #06111e;
  font-weight: 700;
}

.danger {
  background: #ff8b7d;
}

@media (max-width: 760px) {
  .market-page {
    padding: 18px;
  }

  .market-header,
  .toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .toolbar :deep(.el-select) {
    width: 100%;
  }

  .robot-card {
    grid-template-columns: 1fr;
  }
}
</style>
